import { StaticInterface } from '../../../helpers/static-interface';
import EmblaCarousel from 'embla-carousel';
import ClassNames from "embla-carousel-class-names";
import { AlignmentOptionType } from 'embla-carousel/components/Alignment';

export interface ITpxQuoteCarouselBlockUtils {
    loadEmbla(parentEl: HTMLElement): void;
}

export interface IStaticQuoteCarouselBlockUtils {
    new(...args: any[]): ITpxQuoteCarouselBlockUtils;
    init(): void;
}

/**
 * Quote Carousel Block utilities
 * @class
 */
export default class TpxQuoteCarouselBlockUtils
    implements StaticInterface<IStaticQuoteCarouselBlockUtils, typeof TpxQuoteCarouselBlockUtils> {
    constructor(element: HTMLElement) {
        this.loadEmbla(element);
    }

    /**
     * Load Embla
     */
    loadEmbla(parentEl: HTMLElement): void {
        const prefersReducedMotion = window.matchMedia ? window.matchMedia('(prefers-reduced-motion: reduce)').matches : false;

        if (!parentEl || prefersReducedMotion) return;

        const carousel = {
            options: {
                loop: false,
                slidesToScroll: 1,
                inViewThreshold: 1,
                draggable: false,
                align: <AlignmentOptionType>'start',
            },
            components: {
                componentEl: parentEl,
                containerEl: <HTMLElement | null>parentEl.querySelector('[data-component="Carousel"]'),
                slideEls: <NodeList>parentEl.querySelectorAll('[data-component="Slide"]'),
                previousButtonEl: <HTMLElement>parentEl.querySelector('[data-component="PreviousButton"]'),
                nextButtonEl: <HTMLElement>parentEl.querySelector('[data-component="NextButton"]'),
                dotEls: <NodeList>parentEl.querySelectorAll('[data-component="Dot"]'),
            }
        }

        //console.log("Component: Carousel", carousel)

        if (!carousel.components.containerEl) return;

        const embla = EmblaCarousel(carousel.components.containerEl, carousel.options, [ClassNames()]);
        //console.log(embla)

        const prevButton = carousel.components.previousButtonEl;
        const nextButton = carousel.components.nextButtonEl;

        const firstSlide = carousel.components.slideEls[0] as HTMLElement;
        const lastSlide = carousel.components.slideEls[carousel.components.slideEls.length - 1] as HTMLElement;

        const handlePrevButtonInactiveState = () => {
            if (firstSlide.classList.contains("is-selected")) {
                prevButton.classList.add("is-inactive");
            }
            else {
                prevButton.classList.remove("is-inactive");
            }

            if (lastSlide.classList.contains("is-selected")) {
                nextButton.classList.add("is-inactive");
            }
            else {
                nextButton.classList.remove("is-inactive");
            }
        }

        const handleDotsActiveState = () => {
            carousel.components.dotEls.forEach((dot, index) => {
                const correspondingSlide = carousel.components.slideEls[index] as HTMLElement;
                const theDot = dot as HTMLElement;

                if (correspondingSlide.classList.contains("is-selected")) {
                    theDot.classList.add("is-active");
                }
                else {
                    theDot.classList.remove("is-active");
                }
            })
        }


        const setupPrevNextBtnsAndDots = (prevButton, nextButton, embla) => {
            prevButton.addEventListener('click', embla.scrollPrev, false);
            prevButton.addEventListener('click', handlePrevButtonInactiveState);
            prevButton.addEventListener('click', handleDotsActiveState);
            nextButton.addEventListener('click', embla.scrollNext, false);
            nextButton.addEventListener('click', handlePrevButtonInactiveState);
            nextButton.addEventListener('click', handleDotsActiveState);
            handlePrevButtonInactiveState();
            handleDotsActiveState();
        };

        setupPrevNextBtnsAndDots(prevButton, nextButton, embla);
    }

    /**
     * Create new instances of TpxQuoteCarouselBlock for each element with data-component "QuoteCarouselBlock"
     */
    static init(scope?: HTMLElement) {
        [
            ...(scope || document).querySelectorAll('[data-component="QuoteCarouselBlock"]'),
        ].forEach((elem) => {
            new TpxQuoteCarouselBlockUtils(elem as HTMLElement);
        });
    }
}
